import React from 'react';
import Table from '../../Table';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { variables } from 'src/Tenants';
import Status from '../../Status';
import { displayStatusLabel } from 'src/utils/serviceRequestStatus/displayStatusLabel';
import IcoMoon from '../../IcoMoon';
import { ICONS } from 'src/common/constants';
import Label from '../../Label';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import moment from 'moment';
import Search from 'src/components/Search/Search';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import { InboxServiceRequestRow } from 'src/services/api/InboxServiceRequests/apiContracts';
import './inbox-service-requests-list.scss';

const SKELETON_ROW_COUNT: number = 10;

interface InboxServiceRequestsListProps {
  inboxList: any[];
  onStartNewThread: (thread: any) => void;
  serviceRequestsList: InboxServiceRequestRow[];
  loadingServiceRequests: boolean;
  searchString: string;
  onSearch: (e: any) => void;
  onSelectItem: (item: any) => void;
}

const InboxServiceRequestsList = ({
  inboxList,
  onStartNewThread,
  serviceRequestsList,
  loadingServiceRequests,
  searchString,
  onSearch,
  onSelectItem,
}: InboxServiceRequestsListProps) => {
  const { itemsPerPage, onItemsPerPageChange } = useTablePagination(15);

  const onSelectedItem = async (data) => {
    const params = {
      id: data.id,
      referenceId: data.id,
      referenceType: 'Ticket',
      description: data.description,
      ticketNumber: data.ticketNumber,
      message: '',
      propertyName: data?.propertyName || '',
      suiteName: data.suiteNumber,
      itemName: data?.itemName || '',
      issueType: data?.issueType || '',
      isNew: true,
    };
    const existingThread = (inboxList || []).find(
      (item) => item.referenceId === data.id
    );

    if (existingThread && existingThread.id) {
      onSelectItem({ ...params, id: existingThread.id });
      return;
    }

    onStartNewThread(params);
    onSelectItem(params);
  };

  const tableColumns = [
    {
      label: 'Ticket #',
      sortable: true,
      accessor: 'name',
      cell: (data) => (
        <div className="request-link" onClick={() => onSelectedItem(data)}>
          {data.ticketNumber}
        </div>
      ),
    },
    {
      label: `${variables.Suite}`,
      cell: (data) => (
        <div title={data.suiteNumber} className="cut-text">
          {data.suiteNumber}
        </div>
      ),
    },
    {
      label: 'Description',
      cell: (data) => (
        <div title={data.description} className="cut-text">
          {data.description}
        </div>
      ),
    },
    {
      label: 'Status',
      cell: (data) => (
        <Status
          icon={<IcoMoon glyph={ICONS[data.status]} />}
          label={displayStatusLabel(data.status, data.customLabel)}
        />
      ),
    },
    {
      xsHide: true,
      label: 'Submitted Date',
      cell: (data) => {
        const days = moment().diff(moment(data.createdDate), 'days');
        const color = days <= 30 ? 'teal' : days <= 60 ? 'yellow' : 'red';
        return (
          <Label
            color={color}
            label={serviceRequestTableLocalTime(data.createdDate)}
          />
        );
      },
    },
  ];

  const skeletonColumns = tableColumns.map((column) => ({
    ...column,
    cell: () => <Skeleton variant="text" width="70%" />,
  }));

  const skeletonData: any[] = Array.from(
    { length: SKELETON_ROW_COUNT },
    (_: unknown, index: number) => ({
      id: `inbox-sr-skeleton-${index}`,
    })
  );

  const hasRows = serviceRequestsList && serviceRequestsList.length > 0;
  const showTable = loadingServiceRequests || hasRows;

  return (
    <div className="inbox-service-requests-list">
      <div className="requests-panel">
        <div className="search-box">
          <Search
            value={searchString}
            placeholder={`Search ${variables.serviceRequest}s...`}
            onChange={onSearch}
            size={undefined}
            theme={undefined}
          />
        </div>
        {showTable && (
          <div className="requests-table-scroll">
            <Table
              paginate={{ itemsPerPage, position: 'both' }}
              onItemsPerPageChange={onItemsPerPageChange}
              paginationSkeleton={loadingServiceRequests}
              columns={
                loadingServiceRequests ? skeletonColumns : tableColumns
              }
              data={
                loadingServiceRequests ? skeletonData : serviceRequestsList
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxServiceRequestsList;